import { AUTO_PRINT_SCRIPT, escapeHtml } from '../../../lib/printDocument';
import { formatMoney } from '../format';
import { describeStatus } from '../statusLanguage';

/**
 * The printable purchase order (the record's PDF action): a standalone, escaped HTML document
 * opened with openPrintableDocument. Paper colours are fixed on purpose; the document is never
 * themed. Every value from the record goes through escapeHtml.
 */

export type PurchaseOrderDocumentLine = {
  code: string | null;
  name: string;
  /** The line's own description when it differs from the item name. */
  description: string | null;
  qty: number;
  receivedQty: number;
  unitCost: number | null;
  extended: number | null;
  /** "Inventory", "Event sale · 0917061". */
  use: string;
  cancelled: boolean;
};

export type PurchaseOrderDocument = {
  docNo: string | null;
  status: string;
  isR2: boolean;
  kind: 'goods' | 'labor';
  vendorName: string | null;
  vendorContact: { name: string | null; phone: string | null; email: string | null } | null;
  siteName: string | null;
  /** A timestamp or `YYYY-MM-DD`. */
  orderedAt: string | null;
  /** Site-local `YYYY-MM-DD`. */
  expectedDay: string | null;
  orderedBy: string | null;
  eventLabel: string | null;
  serviceTicket: string | null;
  notes: string | null;
  lines: PurchaseOrderDocumentLine[];
  total: number | null;
};

export type PurchaseOrderHtmlOptions = {
  autoPrint?: boolean;
  /** Absolute URL: the document is served from a blob URL, so relative paths do not resolve. */
  logoUrl?: string | null;
  companyName?: string;
  printedAt?: Date;
};

const DEFAULT_COMPANY = 'ITA Audio Visual Solutions';
const DAY_OPTIONS: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
const calendarDayFormatter = new Intl.DateTimeFormat('en-US', { ...DAY_OPTIONS, timeZone: 'UTC' });

/** A site-local `YYYY-MM-DD` printed as is, with its year. */
function calendarDay(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(`${value.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? '—' : calendarDayFormatter.format(parsed);
}

/** A timestamp as the printing viewer's calendar day; a bare `YYYY-MM-DD` stays that day. */
function timestampDay(value: string | null): string {
  if (!value) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return calendarDay(value);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString('en-US', DAY_OPTIONS);
}

function money(value: number | null): string {
  return formatMoney(value) ?? '—';
}

function quantity(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function block(lines: Array<string | null | undefined>): string {
  const present = lines.filter((line): line is string => Boolean(line && line.trim()));
  return present.length ? present.map((line) => escapeHtml(line)).join('<br>') : '—';
}

function lineRow(line: PurchaseOrderDocumentLine, index: number, showReceived: boolean): string {
  const description = line.description && line.description !== line.name
    ? `<span class="po-doc__muted">${escapeHtml(line.description)}</span>`
    : '';
  return `
      <tr${line.cancelled ? ' class="po-doc__cancelled"' : ''}>
        <td class="po-doc__num">${index + 1}</td>
        <td>${line.code ? `<span class="po-doc__code">${escapeHtml(line.code)}</span>` : ''}<strong>${escapeHtml(line.name)}</strong>${description}${line.cancelled ? '<span class="po-doc__tag">Cancelled</span>' : ''}</td>
        <td>${escapeHtml(line.use)}</td>
        <td class="po-doc__num">${escapeHtml(quantity(line.qty))}</td>
        ${showReceived ? `<td class="po-doc__num">${escapeHtml(quantity(line.receivedQty))}</td>` : ''}
        <td class="po-doc__num">${escapeHtml(money(line.unitCost))}</td>
        <td class="po-doc__num">${escapeHtml(money(line.extended))}</td>
      </tr>`;
}

function documentCss(): string {
  return `
    @page { size: Letter portrait; margin: 0.5in; }
    * { box-sizing: border-box; }
    body { margin: 0; background: #fff; color: #111; font-family: Verdana, Arial, Helvetica, sans-serif; }
    .po-doc { max-width: 7.5in; margin: 0 auto; padding: 0.3in 0.1in; font-size: 11px; line-height: 1.35; }
    .po-doc__top { display: grid; grid-template-columns: 1fr auto; gap: 0.3in; align-items: start; padding-bottom: 0.14in; border-bottom: 3px solid #c41130; }
    .po-doc__brand { display: grid; gap: 6px; }
    .po-doc__logo { display: block; width: 1.3in; height: auto; }
    .po-doc__company { font-weight: 700; font-size: 12px; }
    .po-doc__title { margin: 0; font-size: 20px; letter-spacing: 0.02em; }
    .po-doc__meta { margin: 0; display: grid; gap: 3px; font-size: 11px; }
    .po-doc__meta div { display: grid; grid-template-columns: auto 1fr; gap: 10px; }
    .po-doc__meta dt { margin: 0; color: #555; }
    .po-doc__meta dd { margin: 0; font-weight: 700; text-align: right; }
    .po-doc__parties { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.2in; margin: 0.18in 0; }
    .po-doc__parties h2 { margin: 0 0 4px; color: #555; font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; }
    .po-doc__parties p { margin: 0; }
    .po-doc table { width: 100%; border-collapse: collapse; }
    .po-doc th { padding: 5px 6px; border-bottom: 1.5px solid #111; font-size: 9px; letter-spacing: 0.06em; text-align: left; text-transform: uppercase; }
    .po-doc td { padding: 5px 6px; border-bottom: 1px solid #ddd; vertical-align: top; }
    .po-doc td strong { display: block; }
    .po-doc__num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    th.po-doc__num { text-align: right; }
    .po-doc__code { display: block; color: #555; font-size: 9px; letter-spacing: 0.04em; }
    .po-doc__muted { display: block; color: #555; }
    .po-doc__cancelled td { color: #777; }
    .po-doc__cancelled .po-doc__tag { display: inline-block; margin-top: 2px; padding: 0 4px; border: 1px solid #999; border-radius: 3px; color: #555; font-size: 9px; font-weight: 700; text-transform: uppercase; }
    .po-doc__totals { display: grid; justify-content: end; margin-top: 0.12in; }
    .po-doc__totals div { display: grid; grid-template-columns: auto 1.3in; gap: 12px; font-size: 13px; font-weight: 700; }
    .po-doc__totals dt, .po-doc__totals dd { margin: 0; }
    .po-doc__totals dd { text-align: right; font-variant-numeric: tabular-nums; }
    .po-doc__notes { margin-top: 0.2in; padding: 8px 10px; border: 1px solid #ddd; border-radius: 4px; white-space: pre-wrap; }
    .po-doc__notes h2 { margin: 0 0 4px; color: #555; font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; }
    .po-doc__footer { margin-top: 0.25in; color: #666; font-size: 9px; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .po-doc { padding: 0; } }
  `;
}

/** The whole printable document as an HTML string. */
export function buildPurchaseOrderHtml(po: PurchaseOrderDocument, options: PurchaseOrderHtmlOptions = {}): string {
  const company = options.companyName?.trim() || DEFAULT_COMPANY;
  const number = po.docNo || 'Purchase order';
  const status = describeStatus('purchaseOrder', po.status).label;
  const showReceived = po.lines.some((line) => line.receivedQty > 0);
  const contact = po.vendorContact;
  const printedAt = options.printedAt ?? new Date();
  const rows = po.lines.map((line, index) => lineRow(line, index, showReceived)).join('');
  const columns = showReceived ? 7 : 6;
  const logo = options.logoUrl
    ? `<img class="po-doc__logo" src="${escapeHtml(options.logoUrl)}" alt="${escapeHtml(company)}">`
    : '';
  const footer = [
    `Printed ${printedAt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}.`,
    po.isR2 ? 'This purchase order is managed in R2.' : null,
  ].filter(Boolean).join(' ');

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(number)} · Purchase order</title>
  <style>${documentCss()}</style>
</head>
<body>
  <article class="po-doc">
    <header class="po-doc__top">
      <div class="po-doc__brand">
        ${logo}
        <span class="po-doc__company">${escapeHtml(company)}</span>
        <h1 class="po-doc__title">Purchase order ${escapeHtml(number)}</h1>
      </div>
      <dl class="po-doc__meta">
        <div><dt>Ordered</dt><dd>${escapeHtml(timestampDay(po.orderedAt))}</dd></div>
        <div><dt>Expected</dt><dd>${escapeHtml(calendarDay(po.expectedDay))}</dd></div>
        <div><dt>Status</dt><dd>${escapeHtml(status)}</dd></div>
        <div><dt>Type</dt><dd>${po.kind === 'labor' ? 'Labor' : 'Goods'}</dd></div>
      </dl>
    </header>
    <section class="po-doc__parties" aria-label="Vendor and delivery">
      <div>
        <h2>Vendor</h2>
        <p>${block([po.vendorName, contact?.name, contact?.phone, contact?.email])}</p>
      </div>
      <div>
        <h2>Ship to</h2>
        <p>${block([company, po.siteName])}</p>
      </div>
      <div>
        <h2>Ordered by</h2>
        <p>${block([po.orderedBy, po.eventLabel ? `Event ${po.eventLabel}` : null, po.serviceTicket ? `Service ticket ${po.serviceTicket}` : null])}</p>
      </div>
    </section>
    <table aria-label="Order lines">
      <thead>
        <tr>
          <th class="po-doc__num">#</th>
          <th>Item</th>
          <th>Use</th>
          <th class="po-doc__num">Qty</th>
          ${showReceived ? '<th class="po-doc__num">Received</th>' : ''}
          <th class="po-doc__num">Unit cost</th>
          <th class="po-doc__num">Extended</th>
        </tr>
      </thead>
      <tbody>${rows || `<tr><td colspan="${columns}">No lines on this order.</td></tr>`}</tbody>
    </table>
    <dl class="po-doc__totals">
      <div><dt>Order total</dt><dd>${escapeHtml(money(po.total))}</dd></div>
    </dl>
    ${po.notes && po.notes.trim() ? `<section class="po-doc__notes"><h2>Notes</h2>${escapeHtml(po.notes.trim())}</section>` : ''}
    <footer class="po-doc__footer">${escapeHtml(footer)}</footer>
  </article>
  ${options.autoPrint ? AUTO_PRINT_SCRIPT : ''}
</body>
</html>`;
}
